import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch { return iso; }
}

export default function HomePage() {
  const [content, setContent] = useState(null);
  const [events, setEvents] = useState([]);
  const [newsletter, setNewsletter] = useState({ name: "", email: "" });
  const [subStatus, setSubStatus] = useState("idle");

  useEffect(() => {
    api.get("/site-content").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/events").then(({ data }) => {
      const now = new Date();
      const upcoming = data
        .filter((e) => new Date(e.date) >= now)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 3);
      setEvents(upcoming);
    }).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setSubStatus("loading");
    try {
      const { data } = await api.post("/newsletter", newsletter);
      toast.success(data.message || "Subscribed!");
      setNewsletter({ name: "", email: "" });
      setSubStatus("success");
    } catch (err) {
      toast.error(formatErr(err));
      setSubStatus("idle");
    }
  };

  if (!content) {
    return <div className="ser-section text-center">Loading…</div>;
  }

  return (
    <div data-testid="home-page" style={{ backgroundColor: "#fff" }}>
      {/* HERO — slimmer ser.org-style banner image */}
      <section className="relative h-[420px] md:h-[480px] overflow-hidden">
        <img
          src={content.hero_image}
          alt="African landscape"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, rgba(0,43,65,0.78) 0%, rgba(0,43,65,0.45) 55%, rgba(0,43,65,0.15) 100%)",
          }}
        />
        <div className="relative ser-container h-full flex flex-col justify-center px-6 md:px-12 text-white ser-fade-up">
          <p
            className="eyebrow mb-4"
            style={{ color: "#ffffff", opacity: 0.85 }}
            data-testid="hero-eyebrow"
          >
            {content.hero_eyebrow}
          </p>
          <h1
            className="font-serif-display text-3xl md:text-5xl lg:text-6xl leading-[1.15] max-w-3xl mb-5"
            data-testid="hero-title"
            style={{ fontWeight: 300 }}
          >
            {content.hero_title}
          </h1>
          <p
            className="text-base md:text-lg max-w-2xl mb-8 leading-relaxed"
            style={{ color: "rgba(255,255,255,0.92)" }}
            data-testid="hero-subtitle"
          >
            {content.hero_subtitle}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/membership" className="btn-ser-primary" data-testid="hero-cta-membership">
              Become a Member
            </Link>
            <Link
              to="/events"
              className="btn-ser-outline"
              style={{ borderColor: "white", color: "white" }}
              data-testid="hero-cta-events"
            >
              Explore Events
            </Link>
          </div>
        </div>
      </section>

      {/* TOP GREEN BANNER — ser.org signature messaging band */}
      <section className="ser-banner" data-testid="mission-banner">
        <h2>{content.banner_text}</h2>
      </section>

      {/* TOP BOXES — 3-up grid mirroring ser.org "hp-top-boxes" */}
      <section className="ser-section" data-testid="initiatives-section">
        <div className="ser-container">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="eyebrow mb-3">Our Work</p>
            <h2
              className="font-serif-display text-2xl md:text-3xl uppercase tracking-wide"
              style={{ color: "var(--ser-teal)", fontWeight: 300 }}
            >
              Key initiatives shaping restoration in Africa
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            {content.initiatives.slice(0, 3).map((i, idx) => (
              <article
                key={i.id}
                className={`ser-card ser-fade-up ser-delay-${(idx % 3) + 1}`}
                data-testid={`initiative-card-${i.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[var(--ser-section-alt)]">
                  <img src={i.image_url} alt={i.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 text-center flex-1 flex flex-col">
                  <h3
                    className="text-base uppercase mb-3"
                    style={{
                      color: "var(--ser-teal)",
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    {i.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed mb-5 flex-1"
                    style={{ color: "var(--ser-text-muted)" }}
                  >
                    {i.description}
                  </p>
                  <div>
                    <Link to="/about" className="ser-more-btn" data-testid={`initiative-more-${i.id}`}>
                      Learn More
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SECONDARY BANNER */}
      <section
        className="ser-banner"
        style={{ backgroundColor: "var(--ser-teal)" }}
        data-testid="secondary-banner"
      >
        <h2>{content.banner2_text || content.intro_title}</h2>
        <div className="mt-7">
          <Link
            to="/about"
            className="btn-ser-outline"
            style={{ borderColor: "#fff", color: "#fff" }}
            data-testid="secondary-banner-cta"
          >
            Learn More
          </Link>
        </div>
      </section>

      {/* INTRO — image left, body right */}
      <section
        className="ser-section"
        style={{ backgroundColor: "var(--ser-section-alt)" }}
        data-testid="intro-section"
      >
        <div className="ser-container grid md:grid-cols-2 gap-12 items-center">
          <div className="ser-fade-up">
            <img
              src={content.intro_image}
              alt="Restoration work"
              className="w-full aspect-[4/3] object-cover border"
              style={{ borderColor: "var(--ser-border)" }}
            />
          </div>
          <div className="ser-fade-up ser-delay-1">
            <p className="eyebrow mb-3">Who We Are</p>
            <h2
              className="font-serif-display text-2xl md:text-3xl uppercase mb-5"
              style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
            >
              {content.intro_title}
            </h2>
            <p className="leading-relaxed mb-6 text-[0.95rem]" style={{ color: "var(--ser-text-muted)" }}>
              {content.intro_body}
            </p>
            <p className="text-sm mb-6" style={{ color: "var(--ser-teal)" }}>
              <strong>15+ countries · 500+ members · 50+ restoration projects</strong>
            </p>
            <Link to="/about" className="btn-ser-primary" data-testid="intro-learn-more">
              About SER Africa
            </Link>
          </div>
        </div>
      </section>

      {/* NEWS, EVENTS & OPPORTUNITIES */}
      <section className="ser-section" data-testid="upcoming-events-section">
        <div className="ser-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
            <div>
              <p className="eyebrow mb-2">News, Events &amp; Opportunities</p>
              <h2
                className="font-serif-display text-2xl md:text-3xl uppercase"
                style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
              >
                Upcoming Events
              </h2>
            </div>
            <Link to="/events" className="btn-ser-outline self-start" data-testid="view-all-events-link">
              View All Events
            </Link>
          </div>

          {/* Featured: Pre-Symposium Webinar */}
          <a
            href="https://youtu.be/9J-_Lay6a9g"
            target="_blank"
            rel="noopener noreferrer"
            className="ser-card group flex flex-col md:flex-row overflow-hidden mb-8"
            data-testid="home-featured-webinar"
          >
            <div className="md:w-2/5 aspect-video md:aspect-auto relative overflow-hidden bg-black">
              <img
                src="https://img.youtube.com/vi/9J-_Lay6a9g/maxresdefault.jpg"
                onError={(ev) => { ev.currentTarget.src = "https://img.youtube.com/vi/9J-_Lay6a9g/hqdefault.jpg"; }}
                alt="Pre-Symposium Webinar"
                className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: "rgba(255,255,255,0.94)" }}
                  aria-hidden
                >
                  <span
                    style={{
                      display: "inline-block", width: 0, height: 0,
                      borderTop: "10px solid transparent", borderBottom: "10px solid transparent",
                      borderLeft: "16px solid var(--ser-primary-dark)", marginLeft: "4px",
                    }}
                  />
                </span>
              </div>
            </div>
            <div className="md:w-3/5 p-6 md:p-8 flex flex-col">
              <span className="ser-tag self-start mb-3" style={{ backgroundColor: "var(--ser-clay)", color: "white" }}>
                Featured · Pre-Symposium Webinar
              </span>
              <h3
                className="mb-3"
                style={{
                  color: "var(--ser-teal)",
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 700,
                  fontSize: "1.15rem",
                  lineHeight: 1.3,
                  letterSpacing: "0.02em",
                }}
              >
                Living in Balance: Where Nature and People Thrive Together —
                Biosphere Reserves in Action
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--ser-text-muted)" }}>
                Explore how Biosphere Reserves across Africa are connecting people, nature and
                sustainable development while contributing to ecosystem restoration, resilient
                communities and sustainable livelihoods.
              </p>
              <span
                className="text-xs font-bold uppercase tracking-wider mt-auto"
                style={{ color: "var(--ser-primary)" }}
              >
                Watch on YouTube →
              </span>
            </div>
          </a>

          {events.length === 0 ? (
            <p className="text-center py-12" style={{ color: "var(--ser-text-muted)" }}>
              No upcoming events yet — check back soon.
            </p>
          ) : (
            <>
              {/* Featured Conference / Symposium — horizontal card matching the webinar above */}
              {events.filter((e) => e.category === "Conference").map((e) => (
                <Link
                  to="/conferences/symposium-2026"
                  key={e.event_id}
                  className="ser-card group flex flex-col md:flex-row overflow-hidden mb-8"
                  data-testid={`event-preview-${e.event_id}`}
                >
                  <div className="md:w-2/5 aspect-video md:aspect-auto relative overflow-hidden" style={{ backgroundColor: "var(--ser-primary-dark)" }}>
                    <img
                      src={e.image_url}
                      alt={e.title}
                      className="w-full h-full object-contain group-hover:scale-[1.04] transition-transform duration-500"
                    />
                  </div>
                  <div className="md:w-3/5 p-6 md:p-8 flex flex-col">
                    <span className="ser-tag self-start mb-3">{e.category}</span>
                    <h3
                      className="mb-3"
                      style={{
                        color: "var(--ser-teal)",
                        fontFamily: "Lato, sans-serif",
                        fontWeight: 700,
                        fontSize: "1.15rem",
                        lineHeight: 1.3,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {e.title}
                    </h3>
                    <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--ser-text-muted)" }}>
                      {e.description}
                    </p>
                    <div className="flex flex-wrap gap-4 text-xs mt-auto pt-3" style={{ color: "var(--ser-text-muted)" }}>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" /> {formatDate(e.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" /> {e.location}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}

              {/* Remaining events in grid */}
              {events.filter((e) => e.category !== "Conference").length > 0 && (
                <div className="grid md:grid-cols-3 gap-7">
                  {events.filter((e) => e.category !== "Conference").map((e) => (
                    <Link
                      to="/events"
                      key={e.event_id}
                      className="ser-card group"
                      data-testid={`event-preview-${e.event_id}`}
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-[var(--ser-section-alt)]">
                        <img
                          src={e.image_url}
                          alt={e.title}
                          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <span className="ser-tag self-start mb-3">{e.category}</span>
                        <h3
                          className="text-base mb-3 uppercase"
                          style={{
                            color: "var(--ser-teal)",
                            fontFamily: "Lato, sans-serif",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                          }}
                        >
                          {e.title}
                        </h3>
                        <div className="flex flex-wrap gap-3 text-xs mt-auto pt-3" style={{ color: "var(--ser-text-muted)" }}>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" /> {formatDate(e.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> {e.location}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* NEWSLETTER — clean white with teal accents */}
      <section
        className="ser-section"
        style={{ backgroundColor: "var(--ser-section-alt)" }}
        data-testid="newsletter-section"
      >
        <div className="ser-container max-w-2xl text-center">
          <p className="eyebrow mb-3">Stay Connected</p>
          <h2
            className="font-serif-display text-2xl md:text-3xl uppercase mb-5"
            style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
          >
            Restoration News From Across the Continent
          </h2>
          <p className="mb-8 text-[0.95rem]" style={{ color: "var(--ser-text-muted)" }}>
            Get monthly updates on restoration projects, upcoming events, and opportunities —
            straight to your inbox.
          </p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              required
              placeholder="Your name"
              value={newsletter.name}
              onChange={(e) => setNewsletter({ ...newsletter, name: e.target.value })}
              className="ser-input flex-1"
              data-testid="newsletter-name-input"
            />
            <input
              type="email"
              required
              placeholder="Email address"
              value={newsletter.email}
              onChange={(e) => setNewsletter({ ...newsletter, email: e.target.value })}
              className="ser-input flex-1"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              className="btn-ser-primary"
              disabled={subStatus === "loading"}
              data-testid="newsletter-submit-btn"
            >
              {subStatus === "loading" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
